import { Wifi, WifiOff, Delete, Refresh, Info } from "@mui/icons-material";
import { Box, Chip, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid";
import { DeviceSession } from "../../Model/DeviceSession";
import { formatDate } from "../../Util/timeConverter";

function formatUptime(seconds?: number): string {
  if (seconds == null) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// UX 006 — tempo relativo ("há 2 min"); o absoluto vai no tooltip
function timeAgo(iso?: string): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "—";
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  return `há ${Math.floor(h / 24)}d`;
}

// UX 008 — compara versão instalada com a mais recente (version.json)
function versionState(current?: string, latest?: string): "ok" | "old" | "unknown" {
  if (!current || !latest) return "unknown";
  const c = current.split(".").map((n) => parseInt(n, 10) || 0);
  const l = latest.split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(c.length, l.length); i++) {
    const a = c[i] || 0, b = l[i] || 0;
    if (a < b) return "old";
    if (a > b) return "ok";
  }
  return "ok";
}

export function getColumns(
  handleDelete: (params: GridRowParams<DeviceSession>) => void,
  handleForceUpdate?: (params: GridRowParams<DeviceSession>) => void,
  handleOpenDetails?: (params: GridRowParams<DeviceSession>) => void,
  latestAppVersion?: string,
  showClientCol?: boolean,
) {
  const statusCol: GridColDef<DeviceSession> = {
    field: 'status',
    headerName: 'Status',
    width: 104,
    editable: false,
    hideable: false,
    filterable: false,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Chip
          icon={params.value ? <Wifi /> : <WifiOff />}
          label={params.value ? 'Online' : 'Offline'}
          size="small"
          color={params.value ? 'success' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
          sx={{ fontSize: 11 }}
        />
      </Box>
    ),
  };

  // UX 010 — apelido em destaque (principal); modelo como secundário
  const deviceCol: GridColDef<DeviceSession> = {
    field: 'name',
    headerName: 'Dispositivo',
    flex: 2,
    minWidth: 170,
    editable: false,
    renderCell: (params) => {
      const apelido = params.row.name;
      const model = params.row.model ?? params.row.deviceId ?? '—';
      const primary = apelido || model;
      const secondary = apelido ? model : (params.row.androidVersion ?? '');
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', overflow: 'hidden' }}>
          <Box sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {primary}
          </Box>
          <Box sx={{ fontSize: 11.5, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {secondary}
          </Box>
        </Box>
      );
    },
  };

  // UX 003 — só o nome da playlist (o código/hash está no modal de detalhes)
  const playlistCol: GridColDef<DeviceSession> = {
    field: 'playlist',
    headerName: 'Playlist',
    flex: 1.4,
    minWidth: 120,
    editable: false,
    hideable: false,
    filterable: false,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {params.row.playlist?.name}
      </Box>
    ),
  };

  const uptimeCol: GridColDef<DeviceSession> = {
    field: 'uptimeSeconds',
    headerName: 'Ativo há',
    width: 90,
    editable: false,
    filterable: false,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>{formatUptime(params.row.uptimeSeconds)}</Box>
    ),
  };

  const restartsCol: GridColDef<DeviceSession> = {
    field: 'startCount',
    headerName: 'Reinícios',
    width: 84,
    editable: false,
    filterable: false,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>{params.row.startCount ?? '—'}</Box>
    ),
  };

  const ramCol: GridColDef<DeviceSession> = {
    field: 'ramFreeMb',
    headerName: 'RAM livre',
    width: 92,
    editable: false,
    filterable: false,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {params.row.ramFreeMb != null
          ? `${params.row.ramFreeMb > 32768 ? Math.round(params.row.ramFreeMb / 1024) : params.row.ramFreeMb} MB`
          : '—'}
      </Box>
    ),
  };

  // UX 008 — versão colorida: verde=atualizado, amarelo=desatualizado
  const appCol: GridColDef<DeviceSession> = {
    field: 'appVersion',
    headerName: 'App',
    width: 96,
    editable: false,
    filterable: false,
    renderCell: (params) => {
      const st = versionState(params.row.appVersion, latestAppVersion);
      const color = st === 'ok' ? 'success' : st === 'old' ? 'warning' : 'default';
      const tip = st === 'old' ? `Atualização disponível: v${latestAppVersion}` : st === 'ok' ? 'Atualizado' : '';
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title={tip}>
            <Chip
              label={params.row.appVersion ?? '—'}
              size="small"
              color={color as any}
              variant={st === 'unknown' ? 'outlined' : 'filled'}
              sx={{ fontSize: 11 }}
            />
          </Tooltip>
        </Box>
      );
    },
  };

  // UX 006 — "há X min" com o horário exato no tooltip
  const lastSeenCol: GridColDef<DeviceSession> = {
    field: 'lastSeen',
    headerName: 'Último sinal',
    width: 120,
    editable: false,
    hideable: false,
    filterable: false,
    renderCell: (params) => (
      <Tooltip title={formatDate(params.row.lastSeen)}>
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', fontSize: 12.5 }}>
          {timeAgo(params.row.lastSeen)}
        </Box>
      </Tooltip>
    ),
  };

  // UX 004 — largura adequada para o menu de ações (antes estava em 10px)
  const actionsCol: GridColDef<DeviceSession> = {
    field: 'actions',
    type: 'actions',
    width: 56,
    filterable: false,
    sortable: false,
    getActions: (params) => [
      ...(handleOpenDetails
        ? [<GridActionsCellItem icon={<Info />} label="Detalhes / Logs" onClick={() => handleOpenDetails(params)} showInMenu />]
        : []),
      ...(handleForceUpdate && params.row.status && params.row.deviceCode
        ? [<GridActionsCellItem icon={<Refresh />} label="Forçar Atualização" onClick={() => handleForceUpdate(params)} showInMenu />]
        : []),
      <GridActionsCellItem icon={<Delete />} label="Deletar" onClick={() => handleDelete(params)} showInMenu />,
    ],
  };

  const clientCol: GridColDef<DeviceSession> = {
    field: 'userName',
    headerName: 'Cliente',
    width: 130,
    editable: false,
    filterable: false,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', fontSize: 13, color: 'text.secondary' }}>
        {params.row.userName ?? '—'}
      </Box>
    ),
  };

  const columnsDesktop: GridColDef<DeviceSession>[] = showClientCol
    ? [statusCol, clientCol, deviceCol, playlistCol, uptimeCol, restartsCol, ramCol, appCol, lastSeenCol, actionsCol]
    : [statusCol, deviceCol, playlistCol, uptimeCol, restartsCol, ramCol, appCol, lastSeenCol, actionsCol];

  const columnsMobile: GridColDef<DeviceSession>[] = [
    {
      field: 'status',
      headerName: 'St',
      width: 50,
      editable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          {params.value ? <Wifi color="success" fontSize="small" /> : <WifiOff color="error" fontSize="small" />}
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Dispositivo',
      flex: 1,
      editable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', overflow: 'hidden' }}>
          <Box sx={{ fontWeight: 700, fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {params.row.name || params.row.model || params.row.deviceId}
          </Box>
          <Box sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {params.row.playlist?.name}
          </Box>
        </Box>
      ),
    },
    {
      field: 'lastSeen',
      headerName: 'Sinal',
      width: 84,
      editable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title={formatDate(params.row.lastSeen)}>
          <Box sx={{ fontSize: 12 }}>{timeAgo(params.row.lastSeen)}</Box>
        </Tooltip>
      ),
    },
  ];

  return [columnsDesktop, columnsMobile];
}
